/**
 * IST Date Utilities
 * Forces Indian Standard Time (Asia/Kolkata, UTC+5:30) for all
 * date/time formatting regardless of the user's computer timezone.
 */

const IST = 'Asia/Kolkata';
const LOCALE = 'en-IN';

/** Format a date as  "22 Jul 2026" */
export function fmtDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(LOCALE, {
    timeZone: IST,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Format a date+time as "22 Jul 2026, 03:30 PM" */
export function fmtDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(LOCALE, {
    timeZone: IST,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Format time only as "03:30 PM" */
export function fmtTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString(LOCALE, {
    timeZone: IST,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Format month + year as "Jul 2026" */
export function fmtMonthYear(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(LOCALE, {
    timeZone: IST,
    month: 'short',
    year: 'numeric',
  });
}

/** Format month name only as "July" */
export function fmtMonthName(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(LOCALE, {
    timeZone: IST,
    month: 'long',
  });
}

/** Format month short as "Jul" */
export function fmtMonthShort(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(LOCALE, {
    timeZone: IST,
    month: 'short',
  });
}

/**
 * Get current date/time as an ISO string in IST
 * (useful for default form values)
 */
export function nowIST() {
  return new Date().toLocaleString(LOCALE, { timeZone: IST });
}

/**
 * Get today's date as YYYY-MM-DD in IST
 */
export function todayISODate() {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: IST }));
  const y = ist.getFullYear();
  const m = String(ist.getMonth() + 1).padStart(2, '0');
  const d = String(ist.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Convert any date value to YYYY-MM-DD (timezone-safe)
 */
export function toIsoDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** Check if a date is overdue (in IST) */
export function isOverdue(value) {
  if (!value) return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

// ─── Aliases for drop-in compatibility with finance/utils/date.js ───
export const formatDate = fmtDate;
export const formatDateTime = fmtDateTime;

/**
 * Returns the Financial Year start year in IST (April 1 to March 31).
 * E.g., Oct 2026 -> 2026; Feb 2026 -> 2025.
 */
export function getCurrentFinancialYear(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  const ist = new Date(d.toLocaleString('en-US', { timeZone: IST }));
  return ist.getMonth() >= 3 ? ist.getFullYear() : ist.getFullYear() - 1;
}

/** Formats financial year label as "FY 2025–26" */
export function getFinancialYearLabel(fy) {
  const nextShort = String((Number(fy) + 1) % 100).padStart(2, '0');
  return `FY ${fy}–${nextShort}`;
}

